import React, { Component } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import MarkerClusterer from '@google/markerclustererplus';
import {
  centerMap,
  changeMarkerIcon,
  loadMarkers,
  displayCurrentLocation,
  waitForGoogle,
} from '../../utils/map';
import { RetailersStore, MarkerAction, OnStoreHover, ActiveRetailer } from 'src/modules/retailer';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { FormattedMessage } from 'react-intl';
import { setMap, changeMapBounds, setMarkers } from '../../store/actions';
import { ActionCreatorType } from 'src/store';
import { OutputChannels } from 'src/modules/locale';
import styles from './RetailerMap.module.scss';
import { BaseStore } from 'src/store';
import mapStyles from './mapStyles';

// Trick to avoid error with window.initMap from google maps async load
const globalAny: any = global;

interface RetailerMapProps extends RetailersStore {
  activeRetailer: ActiveRetailer;
  onRetailerHover: OnStoreHover;
  setMap: ActionCreatorType;
  changeMapBounds: ActionCreatorType;
  setMarkers: ActionCreatorType;
  style?: React.CSSProperties;
  containerRef?: any;
  className?: string;
  outputChannel: OutputChannels;
}

interface RetailerMapState {
  markersLoaded: 'NOT_LOADED' | 'LOADED';
}

export class RetailerMap extends Component<RetailerMapProps, RetailerMapState> {
  markerClusterer?: MarkerClusterer;
  constructor(props: RetailerMapProps) {
    super(props);
    this.state = {
      markersLoaded: 'NOT_LOADED',
    };
  }

  componentDidUpdate(prevProps: RetailerMapProps) {
    const {
      map,
      activeRetailer: { id: activeRetailerId },
    } = this.props;
    const {
      activeRetailer: { id: prevActiveStoreId },
    } = prevProps;

    waitForGoogle(() => {
      // Load markers only once after retailers are loaded
      if (this.state?.markersLoaded === 'NOT_LOADED' && this.props.retailers.length) {
        loadMarkers(map, this.props);
        displayCurrentLocation(map);
        this.setState({ markersLoaded: 'LOADED' });
      }

      const { markers } = this.props;

      if (!markers.length) return;
      if (prevActiveStoreId && activeRetailerId !== prevActiveStoreId) {
        changeMarkerIcon(prevActiveStoreId, MarkerAction.Deactivate, markers);
      }
      if (activeRetailerId !== prevActiveStoreId) {
        changeMarkerIcon(activeRetailerId, MarkerAction.Activate, markers);
      }
    });
  }

  componentDidMount() {
    if (window.google && window.google.maps) {
      if (document.getElementsByClassName('gm-style').length) {
        return;
      } else {
        return this.initMap();
      }
    }

    // Create the script tag, set the appropriate attributes
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY}&libraries=places,geometry&callback=initMap`;
    script.defer = true;
    script.async = true;

    // Attach your callback function to the `window` object
    globalAny.window.initMap = this.initMap.bind(this);

    // Append the 'script' element to 'head'
    document.head.appendChild(script);
  }

  initMap() {
    let initialMapProps;
    switch (this.props.outputChannel) {
      case OutputChannels.AU:
        initialMapProps = {
          center: { lat: -25, lng: 135 },
          zoom: 5,
        };
        break;
      case OutputChannels.US:
        initialMapProps = {
          center: { lat: 40, lng: -97 },
          zoom: 4,
        };
        break;
      case OutputChannels.DE:
      default:
        initialMapProps = {
          center: { lat: 51, lng: 10 },
          zoom: 7,
        };
        break;
    }
    const map = new google.maps.Map(document.getElementById('map'), initialMapProps);
    map.setOptions({ styles: mapStyles });
    map.addListener('bounds_changed', () => {
      this.props.changeMapBounds();
    });
    // Display the relevant map location
    if (this.props.currentLocation) {
      centerMap(map, this.props.currentLocation);
    }
    this.props.setMap(map);
  }

  render() {
    return (
      <div
        className={classnames(styles.container, this.props.className)}
        style={this.props.style}
        ref={this.props.containerRef}>
        <div>
          <div id="map" className={styles.map} />
          <div className={styles.iconContainer}>
            <div className={styles.icon}>
              <GlobalIcon
                image={IconImage.WkGMapsBrandSpace}
                size="custom"
                className={styles.iconBig}
              />
            </div>{' '}
            <span>
              <FormattedMessage id="page/store/brand_space" />
            </span>
            <GlobalIcon image={IconImage.WkGMapsStore} size="custom" className={styles.iconBig} />{' '}
            <span>
              <FormattedMessage id="page/store/stores" />
            </span>
          </div>
          {/* <div id="infowindow-content">
          <img src="" width="16" height="16" id="place-icon" />
          <span id="place-name" className="title"></span>
          <br />
          <span id="place-address"></span>
        </div> */}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: BaseStore) {
  return { ...state.retail };
}

export default connect(mapStateToProps, {
  setMap,
  changeMapBounds,
  setMarkers,
})(RetailerMap);
